import React, { useCallback, useMemo } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { EstimateSplitCardProps } from "../types";
import { calculateSupplierFulfillingItemsMRP } from "../utils/deliveryItemUtils";
import { useDeliverySelectionValidation } from "../validation/useDeliverySelectionValidation";
import { useManualOptionHandlers } from "../hooks/useManualOptionHandlers";
import { MANUAL_DELIVERY_OPTION_RADIO_VALUE } from "../utils/deliveryOptionSelectionUtils";
import { useEstimateSplitSelection } from "./hooks/useEstimateSplitSelection";
import { useEstimateSplitMutations } from "./hooks/useEstimateSplitMutations";
import { useBuyTimeInput } from "./hooks/useBuyTimeInput";
import { SupplierSplitHeader } from "./components/SupplierSplitHeader";
import { SplitActionPills } from "./components/SplitActionPills";
import { PartsListColumn } from "./components/PartsListColumn";
import { DeliveryOptionsPanel } from "./components/DeliveryOptionsPanel";
import { getFullPaymentRouteLabel } from "./utils/paymentRouteLabels";
import { ReadOnlySplitActionDisplay } from "./components/ReadOnlySplitActionDisplay";

const EstimateSplitCard: React.FC<EstimateSplitCardProps> = ({
  estimateNo,
  supplier,
  selection,
  onSelectionChange,
  isExpired,
  quickEtasMins,
  instantDeliveryMinimumDeliveryCost,
  orderData,
  garaazSupplierIdFromConfig,
  isSingleGaraazInvoiceRequired,
  onOptionAdd,
  onOptionDelete,
  onSplitConfigApplied,
  isReadOnly = false,
}) => {
  const isRemainingItems =
    supplier.supplierId === null || supplier.supplierId === undefined;

  const supplierFulfillingItemsMRP = useMemo(
    () => calculateSupplierFulfillingItemsMRP(supplier),
    [supplier],
  );

  const { errorsByField } = useDeliverySelectionValidation(selection, {
    instantDeliveryMinimumDeliveryCost,
  });

  const splitSelection = useEstimateSplitSelection({
    supplier,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
  });

  const buyTime = useBuyTimeInput({
    initialBuyTimeMinutes: supplier.buyTime,
    supplierId: supplier.supplierId,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    isExpired,
  });

  const mutations = useEstimateSplitMutations({
    estimateNo,
    supplier,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    onOptionDelete: onOptionDelete || (async () => {}),
    onOptionAdd: onOptionAdd || (() => {}),
    instantDeliveryMinimumDeliveryCost,
    isManualSelected: splitSelection.isManualSelected,
    isExpired,
  });

  const {
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
    handleManualCPTSlabChange,
    handleManualSupplierChange,
  } = useManualOptionHandlers({
    isExpired,
    quickEtasMins,
    currentManualEntry: selection?.deliveryDetails?.manualEntry,
    onManualEntryChange: splitSelection.handleManualOptionUpdate,
  });

  const displayTitle = isRemainingItems
    ? "Supplier Not Found"
    : supplier.supplierName;

  const paymentRouteLabel = useMemo(
    () =>
      getFullPaymentRouteLabel(supplier.payableBy, supplier.payableTo),
    [supplier.payableBy, supplier.payableTo],
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      if (value === MANUAL_DELIVERY_OPTION_RADIO_VALUE) {
        splitSelection.handleManualOptionSelect();
      } else {
        splitSelection.handleDeliveryOptionSelect(value);
      }
    },
    [splitSelection],
  );

  const handleManualEntryCancel = useCallback(() => {
    splitSelection.handleManualEntryCancel(isExpired);
  }, [splitSelection, isExpired]);

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="orange.300"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: "md" }}
    >
      <SupplierSplitHeader
        displayTitle={displayTitle}
        supplier={supplier}
        isRemainingItems={isRemainingItems}
        supplierFulfillingItemsMRP={supplierFulfillingItemsMRP}
        actions={
          isReadOnly ? (
            <ReadOnlySplitActionDisplay
              status={supplier.status}
              buyTimeMinutes={supplier.buyTime}
            />
          ) : (
            <SplitActionPills
              selection={selection}
              isExpired={isExpired}
              buyTimeValue={buyTime.buyTimeValue}
              isBuyTimeSelected={splitSelection.isBuyTimeSelected}
              onHold={splitSelection.handleHold}
              onCancel={splitSelection.handleCancel}
              onBuyTimeToggle={buyTime.handleBuyTimeToggle}
              onBuyTimeValueChange={buyTime.handleBuyTimeValueChange}
            />
          )
        }
      />

      <Flex direction={{ base: "column", md: "row" }}>
        <PartsListColumn
          parts={supplier.parts}
          estimateNo={estimateNo}
          splitId={supplier.splitId}
          supplierId={supplier.supplierId}
          orderData={orderData}
          garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
          isSingleGaraazInvoiceRequired={isSingleGaraazInvoiceRequired}
          isRemainingItems={isRemainingItems}
          transactionType={supplier.transactionType}
          discountPercentage={supplier.discountPercentage}
          creditNotePercentage={supplier.creditNotePercentage}
          paymentRouteFromLabel={paymentRouteLabel.from}
          paymentRouteToLabel={paymentRouteLabel.to}
          payableBy={supplier.payableBy}
          payableTo={supplier.payableTo}
          isExpired={isExpired}
          onSplitConfigApplied={onSplitConfigApplied || (() => {})}
          isReadOnly={isReadOnly}
        />
        <DeliveryOptionsPanel
          supplier={supplier}
          selection={selection}
          estimateNo={estimateNo}
          isExpired={isExpired}
          isRemainingItems={isRemainingItems}
          radioValue={splitSelection.radioValue}
          isManualSelected={splitSelection.isManualSelected}
          isDeliveryOptionSelected={splitSelection.isDeliveryOptionSelected}
          errorsByField={errorsByField}
          quickEtaOptions={quickEtaOptions}
          manualSaveError={mutations.manualSaveError}
          isSavingManualOption={mutations.isSavingManualOption}
          isDeleteDisabled={mutations.isDeleteDisabled}
          isDeletingOption={mutations.isDeletingOption}
          onRadioChange={handleRadioChange}
          onDeliveryOptionSelect={splitSelection.handleDeliveryOptionSelect}
          onManualOptionSelect={splitSelection.handleManualOptionSelect}
          onOptionDelete={mutations.handleOptionDelete}
          onManualCPTSlabChange={handleManualCPTSlabChange}
          onManualCPTChange={handleManualCPTChange}
          onManualChargeChange={handleManualChargeChange}
          onQuickEtaSelect={handleQuickEtaSelect}
          onManualEntryCancel={handleManualEntryCancel}
          onManualEntrySave={mutations.handleManualEntrySave}
          onManualSupplierChange={handleManualSupplierChange}
          onOptionSupplierChange={splitSelection.handleOptionSupplierChange}
          isReadOnly={isReadOnly}
        />
      </Flex>
    </Box>
  );
};

export default EstimateSplitCard;
